import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { MutableRefObject, useEffect, useState } from 'react';
import MigrationItemViewDTO from '@src/adapters/dto/migration/MigrationItemViewDTO';
import useSaveMigrationItemDetailsLazy from '@src/adapters/hooks/migration/useSaveMigrationItemDetails';
import MigrationItemUpdateDTO from '@src/adapters/dto/migration/MigrationItemUpdateDTO';
import { MigrationStatus } from '@src/constants/MigrationStatus';

interface MigrationItemNoteDTO {
  id: string;
  note: string;
  open: number;
  onHand: number;
  isLoan: boolean;
}

interface Props {
  item: MigrationItemViewDTO;
  open: boolean;
  anchorEl: MutableRefObject<null> | null;
  status: MigrationStatus;
  cancel(): void;
  close(item: MigrationItemUpdateDTO): void;
  deleteItem(id: string): void;
}

function CardItemDetails({
  item,
  open,
  anchorEl,
  status,
  cancel,
  close,
  deleteItem,
}: Props) {
  const [defaultValue, setDefaultValue] = useState('');
  const { execute: save } = useSaveMigrationItemDetailsLazy();

  const { control, handleSubmit, setValue } = useForm<MigrationItemNoteDTO>();

  const onSubmit = async (data: MigrationItemNoteDTO) => {
    await save({
      id: data.id,
      isLoan: data.isLoan,
      note: data.note,
      onHand: data.onHand,
      open: data.open,
    });

    const udpatedItem: MigrationItemUpdateDTO = {
      id: data.id,
      isLoan: data.isLoan,
      note: data.note,
      onHand: data.onHand,
      open: data.open,
    };

    close(udpatedItem);
  };

  useEffect(() => {
    setValue('id', item.id);
    setValue('open', item.qtyOpen);
    setValue('onHand', item.qtyOnHand);
    setValue('isLoan', item.isLoan);
    if (item.note) {
      setValue('note', item.note);
      setDefaultValue(item.note);
    } else {
      setValue('note', '');
      setDefaultValue('');
    }
  }, [item, setValue]);

  const handleDeleteItem = () => {
    deleteItem(item.id);
    cancel();
  };

  return (
    <Popover
      open={open}
      aria-labelledby="Note"
      aria-describedby="note"
      disableEnforceFocus
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      anchorEl={anchorEl ? anchorEl?.current : null}>
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}>
        <Typography variant="h6">Item Details</Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <Box sx={(theme) => ({ padding: theme.spacing(2), width: '100%' })}>
            <TextField
              label="Item"
              fullWidth
              disabled
              value={item.inventory.name}
            />
          </Box>
          <Box
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'left',
              width: '100%',
              padding: theme.spacing(2),
            })}>
            <Controller
              name="open"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  sx={{ width: '48%' }}
                  label="Open"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: 0,
                  }}
                  disabled={status === MigrationStatus.Complete}
                />
              )}
            />
            <Controller
              name="onHand"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  sx={{ width: '48%' }}
                  label="On Hand"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: 0,
                  }}
                  disabled={status === MigrationStatus.Complete}
                />
              )}
            />
          </Box>
          <Box sx={(theme) => ({ padding: theme.spacing(2), width: '100%' })}>
            <Controller
              name="note"
              control={control}
              defaultValue={defaultValue}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  multiline
                  fullWidth
                  rows={10}
                  disabled={status === MigrationStatus.Complete}
                  sx={(theme) => ({
                    height: '250px',
                  })}
                />
              )}
            />
          </Box>
          <Box sx={(theme) => ({ padding: theme.spacing(2), width: '100%' })}>
            <Controller
              name="isLoan"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={status === MigrationStatus.Complete}
                    />
                  }
                  label="Mark as Loan"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ flex: '1', marginRight: '8px' }}>
          <Button
            onClick={() => cancel()}
            color="primary"
            variant="outlined"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Cancel
          </Button>
          {status !== MigrationStatus.Complete && (
            <>
              <Button
                onClick={() => handleDeleteItem()}
                color="error"
                variant="contained"
                sx={{ flex: '1', marginLeft: '8px' }}>
                Remove
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{ flex: '1', marginLeft: '8px' }}>
                Update
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Popover>
  );
}

export default CardItemDetails;
